<template>
  <div class="god_page_container">
    <div class="page-title-row">
      <h2>Gestión de Páginas (SEO)</h2>
    </div>

    <div id="selector_container">
      <input
        type="text"
        class="input_200"
        v-model="search_text"
        placeholder="Buscar por url..."
      />

      <div class="radio-pill-group">
        <label
          class="radio-pill"
          :class="{ 'is-selected': pageType === 'gift' }"
        >
          <input
            type="radio"
            value="gift"
            v-model="pageType"
          />
          <span>Regalos</span>
        </label>
        <label
          class="radio-pill"
          :class="{ 'is-selected': pageType === 'evergreen' }"
        >
          <input
            type="radio"
            value="evergreen"
            v-model="pageType"
          />
          <span>Evergreen</span>
        </label>
        <label
          class="radio-pill"
          :class="{ 'is-selected': pageType === 'seasonal' }"
        >
          <input
            type="radio"
            value="seasonal"
            v-model="pageType"
          />
          <span>Estacionales</span>
        </label>
      </div>

      <button @click="addNewPage()" class="main_button-green" style="margin-left: auto;" :style="pageType == 'gift' ? { opacity: '0.3' } : 'block'">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        Añadir página
      </button>
    </div>
    
    <div class="table-container">
      <table>
        <thead>
          <tr>
              <th class="big">Título</th>
              <th class="small">Url</th>
              <th class="small" v-if="pageType === 'gift' || pageType === 'evergreen'">Número de planes</th>
              <th class="small" v-if="pageType === 'seasonal'">Rango de fechas</th>
              <th class="small">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="(item, index) in filteredPages"
            :key="item.url"
            :class="index % 2 === 0 ? 'bg-gray-100' : 'bg-white'"
          >
            <td>{{ item.title }}</td>
            <td>{{ item.url }}</td>
            <td v-if="pageType === 'gift' || pageType === 'evergreen'">{{ item.num_plans }}</td>
            <td v-if="pageType === 'seasonal'">{{ formatDate(item.start_date) }} - {{ formatDate(item.end_date) }}</td>
            <td>
              <div class="actions-cell">
                <img class="action_icon" title="Ver" src="@/assets/images/ic_eye.png" @click="onVisitPageClicked(item)"/>
                <img class="action_icon" title="Editar" src="@/assets/images/ic_action_edit.png" @click="onEditPageClicked(item)"/>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ErrorPopup :errorMessage="error" />
    <TwoButtonPopup
      :title="twoButtonTitle"
      :message="twoButtonMessage"
      :acceptButton="twoButtonAcceptButton"
      @acceptCallback="() => twoButtonConfirmed()"
      :cancelButton="twoButtonCancelButton"
      @cancelCallback="() => twoButtonCancelled()"
      />
    <GiftPageDetailsPopup :page="selectedGiftPage" @closed="onGiftPageClosed" @saved="onGiftPageSaved" @error="onGiftPageError" />
    <EvergreenPageDetailsPopup :page="selectedEvergreenPage" @closed="onEvergreenPageClosed" @saved="onEvergreenPageSaved" @error="onEvergreenPageError" />
    <SeasonalPageDetailsPopup :page="selectedSeasonalPage" @closed="onSeasonalPageClosed" @saved="onSeasonalPageSaved" @error="onSeasonalPageError" />
  </div>

</template>

<script setup>
  import { ref, nextTick, watch } from 'vue'
  import { useLoadingStore } from "@/stores/loadingStore";
  import { useRouter } from 'vue-router';
  import { refreshToken } from "@/services/authService";
  import ErrorPopup from "@/components/common/ErrorPopup.vue";
  import TwoButtonPopup from "@/components/common/TwoButtonPopup.vue";
  import GiftPageDetailsPopup from './GiftPageDetailsPopup.vue';
  import EvergreenPageDetailsPopup from './EvergreenPageDetailsPopup.vue';
  import SeasonalPageDetailsPopup from './SeasonalPageDetailsPopup.vue';

  const pageType = ref('gift');
  const filteredPages = ref([]);
  const pages = ref([]);
  const search_text = ref("");

  const error = ref(null);
  const loadingStore = useLoadingStore();
  const router = useRouter();
  const selectedGiftPage = ref(null);
  const selectedEvergreenPage = ref(null);
  const selectedSeasonalPage = ref(null);

  const twoButtonTitle = ref("");
  const twoButtonMessage = ref("");
  const twoButtonAcceptButton = ref("");
  const twoButtonCancelButton = ref("");
  let twoButtonConfirmed = null;
  let twoButtonCancelled = null;

  const addNewPage = () => {
    if (pageType.value == 'evergreen') {
      selectedEvergreenPage.value = {
        id: null,
        url: "",
        title: "",
        description: "",
        header_picture: null,
        footer_title: "",
        footer_description: "",
        seo_title: "",
        seo_description: "",
        num_plans: 0,
        plans: "",
        index: false
      }
    } else if (pageType.value == 'seasonal') {
      selectedSeasonalPage.value = {
        id: null,
        url: "",
        title: "",
        description: "",
        header_picture: null,
        footer_title: "",
        footer_description: "",
        seo_title: "",
        seo_description: "",
        start_date: null,
        end_date: null,
        index: false
      }
    }

  
  };

  const getPages = async () => {
    loadingStore.setLoading(true);
    error.value = null;

    try {
      let response;
      if (pageType.value === 'gift') {
        response = await getAllGiftPagesInAPI();
      } else if (pageType.value === 'evergreen') {
        response = await getAllEvergreenPagesInAPI();
      } else if (pageType.value === 'seasonal') {
        response = await getAllSeasonalPagesInAPI();
      }

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          if (pageType.value === 'gift') {
            response = await getAllGiftPagesInAPI();
          } else if (pageType.value === 'evergreen') {
            response = await getAllEvergreenPagesInAPI();
          } else if (pageType.value === 'seasonal') {
            response = await getAllSeasonalPagesInAPI();
          }
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        showError("Se ha producido un error cargando las páginas SEO.");
        return;
      }

      const data = await response.json();

      if (data.errors) {
        error.value = data.message;
        pages.value = [];
        filterPages();
      } else {
        error.value = null;
        pages.value = data.pages;
        filterPages();
      }
    } catch (exception) {
      error.value = exception.message;
    } finally {
      loadingStore.setLoading(false);
    }
  };

  async function getAllGiftPagesInAPI() {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/pages/get_gift_pages?user_id="+localStorage.getItem('user_id'), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
      });
  }

  async function getAllEvergreenPagesInAPI() {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/pages/get_evergreen_pages?user_id="+localStorage.getItem('user_id'), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
      });
  }

  async function getAllSeasonalPagesInAPI() {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/pages/get_seasonal_pages?user_id="+localStorage.getItem('user_id'), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
      });
  }

  const showError = async (message) => {
    error.value = null;
    await nextTick();
    error.value = message;
  };

  watch(pageType, () => {
    getPages();
  });

  watch(search_text, () => {
    filterPages();
  });

  const filterPages = () => {
    filteredPages.value = pages.value.filter((page) => {
      return page.url.toLowerCase().includes(search_text.value.toLowerCase());
    });
  };

  const onVisitPageClicked = async (page) => {
    window.open(page.full_url, '_blank');
  }

  const onEditPageClicked = async (page) => {
    loadingStore.setLoading(true);
    error.value = null;

    try {
      let response = null;
      if (pageType.value === 'gift') {
        response = await getGiftPageDetailsInAPI(page);
      } else if (pageType.value === 'evergreen') {
        response = await getEvergreenPageDetailsInAPI(page);
      } else if (pageType.value === 'seasonal') {
        response = await getSeasonalPageDetailsInAPI(page);
      }

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          if (pageType.value === 'gift') {
            response = await getGiftPageDetailsInAPI(page);
          } else if (pageType.value === 'evergreen') {
            response = await getEvergreenPageDetailsInAPI(page);
          } else if (pageType.value === 'seasonal') {
            response = await getSeasonalPageDetailsInAPI(page);
          }
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        showError("Se ha producido un error obteniendo los detalles de la página.");
        return;
      }

      const data = await response.json();

      if (data.errors) {
        error.value = data.message;
        selectedGiftPage.value = null;
        selectedEvergreenPage.value = null;
        selectedSeasonalPage.value = null;
      } else {
        error.value = null;
        if (pageType.value === 'gift') {
          selectedGiftPage.value = data.page;
        } else if (pageType.value === 'evergreen') {
          selectedEvergreenPage.value = data.page;
        } else if (pageType.value === 'seasonal') {
          selectedSeasonalPage.value = data.page;
        }
      }
    } catch (exception) {
      error.value = exception.message;
    } finally {
      loadingStore.setLoading(false);
    }
  };

  async function getGiftPageDetailsInAPI(page) {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/pages/get_gift_page_details?user_id="+localStorage.getItem('user_id')+"&url="+page.url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
      });
  }

  async function getEvergreenPageDetailsInAPI(page) {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/pages/get_evergreen_page_details?user_id="+localStorage.getItem('user_id')+"&url="+page.url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
      });
  }

  async function getSeasonalPageDetailsInAPI(page) {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/pages/get_seasonal_page_details?user_id="+localStorage.getItem('user_id')+"&url="+page.url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
      });
  }

  const onGiftPageClosed = () => {
    selectedGiftPage.value = null;
  };

  const onGiftPageSaved = () => {
    getPages();
    selectedGiftPage.value = null;
  };

  const onGiftPageError = (error) => {
    showError(error);
  };

  const onEvergreenPageClosed = () => {
    selectedEvergreenPage.value = null;
  };

  const onEvergreenPageSaved = () => {
    getPages();
    selectedEvergreenPage.value = null;
  };

  const onEvergreenPageError = (error) => {
    showError(error);
  };

  const onSeasonalPageClosed = () => {
    selectedSeasonalPage.value = null;
  };

  const onSeasonalPageSaved = () => {
    getPages();
    selectedSeasonalPage.value = null;
  };

  const onSeasonalPageError = (error) => {
    showError(error);
  };

  const formatDate = (val) => {
    if (!val) return '';
    const [year, month, day] = val.split('-');
    return `${day}/${month}/${year}`;
  };
  getPages();

</script>

<style scoped>
.radio-pill-group {
  display: flex;
  background: var(--very-clear-gray);
  padding: 4px;
  border-radius: var(--radius-md);
  gap: 4px;
}

.radio-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: var(--radius-sm);
  font-size: 13px;
  font-weight: 600;
  color: var(--text-color-light);
  cursor: pointer;
  transition: all var(--transition-fast);
  user-select: none;
}

.radio-pill input[type="radio"] {
  display: none;
}

.radio-pill.is-selected {
  background: #FFFFFF;
  color: var(--text-color-dark);
  box-shadow: var(--shadow-xs);
}

.radio-pill.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.text-subtle {
  font-size: 12px;
  color: var(--text-color-light);
}

.actions-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.big {
  width: 25%;
}

.small {
  width: 15%;
}

@media (max-width: 768px) {
  #selector_container > * {
    flex: none !important;
    width: 100%;
  }

  .radio-pill-group {
    flex-direction: column;
  }

  .actions-cell {
    flex-wrap: wrap;
    gap: 6px;
  }
}
</style>
